/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo } from 'react';

import { cleanString } from '../helpers/cleanString';
import { FreshchatPlatform } from '../model/enum/FreshchatPlatform.enum';
import { useUpdateUserFreshChatRestoreId } from '../services/myProfile/useUpdateUserFreshChatRestoreId';

declare global {
  interface Window {
    fcWidget: any;
    fcWidgetMessengerConfig: any;
  }
}

type FreshChatConfig = {
  mode: 'auto' | 'manual';
  autoShowDelay?: number;
};

export const useFreshChat = (config?: FreshChatConfig) => {
  const mode = config?.mode ?? 'auto';
  const autoShowDelay = config?.autoShowDelay ?? 5000;

  const rawUserData = localStorage.getItem('userData');
  const userData = rawUserData ? JSON.parse(rawUserData) : null;
  const hasSpentBefore = useMemo(
    () => userData?.has_spent_before ?? false,
    [userData?.has_spent_before]
  );
  const freshchatPlatform = useMemo(
    () => userData?.freshdesk_platform ?? FreshchatPlatform.WIDGET,
    [userData?.freshdesk_platform]
  );
  const { mutate: saveRestoreId } = useUpdateUserFreshChatRestoreId();
  const restoreId = userData?.freshchat_restore_id ?? null;

  const _innerShowWidget = (open: boolean = false) => {
    if (window.fcWidget) {
      window.fcWidget.setConfig({ config: { headerProperty: { hideChatButton: false } } });
      window.fcWidget.show();
      if (open) {
        setTimeout(() => {
          window.fcWidget.open();
        }, 50);
      }
    }
  };

  const _innerHideWidget = () => {
    if (window.fcWidget) {
      window.fcWidget.setConfig({ config: { headerProperty: { hideChatButton: true } } });
      window.fcWidget.close();
      window.fcWidget.hide();
    }
  };

  useEffect(() => {
    const platform = cleanString(freshchatPlatform);
    const isContactPage = window.location.pathname === '/members-area/contact-us';
    const isVerificationPage = window.location.pathname === '/email-verification-instruction';
    const isAllowedPage = isContactPage || isVerificationPage;

    let timeoutId: NodeJS.Timeout | null = null;

    const shouldShowWidgetLauncher =
      isContactPage && hasSpentBefore && platform === FreshchatPlatform.CHAT;

    if (window.fcWidget) {
      // if widget was previously shown and now user goes away from the page we should hide it
      if (!isAllowedPage) {
        _innerHideWidget();
      }
      // if widget was previously hidden and now user goes to the state when it should be shown
      if (shouldShowWidgetLauncher) {
        _innerShowWidget();
      }
    }

    // Initialize widget if:
    // - On verification page (always allowed)
    // - On contact page (requires hasSpentBefore and correct platform)
    if (
      window.fcWidget &&
      (isVerificationPage ||
        (isContactPage && hasSpentBefore && platform === FreshchatPlatform.CHAT))
    ) {
      window.fcWidget.init({
        token: '98a0a2f9-e360-495e-9f36-c88b0addb4ee',
        host: 'https://cdncloud-help.freshchat.com',
        widgetUuid: 'd4767699-f7df-417d-8031-22f6e2cad441',
        externalId: userData?.id ?? 'Unknown ID',
        restoreId,
        firstName: userData?.profile?.username ?? 'Unknown name',
        email: userData?.email ?? 'Unknown email',
        // even tho widget should be initialized initial display settings for button depend on page
        // for example EVI page should not show widget button unless there is button to ask for help
        config: { headerProperty: { hideChatButton: !shouldShowWidgetLauncher } }
      });

      window.fcWidget.on('user:created', async (resp: any) => {
        const { status, data } = resp;
        if (status === 200 && data.restoreId && userData?.id) {
          saveRestoreId(data.restoreId);
        }
      });

      // Auto-show widget ONLY on contact page in auto mode
      if (isContactPage && mode === 'auto') {
        timeoutId = setTimeout(() => {
          _innerShowWidget(true);
        }, autoShowDelay);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      _innerHideWidget();
      if (window.fcWidget) {
        // cleanup widget remains
        window.fcWidget.off('user:created');
      }
    };
  }, [
    mode,
    autoShowDelay,
    userData?.id,
    hasSpentBefore,
    freshchatPlatform,
    restoreId,
    saveRestoreId,
    window.location.pathname
  ]);

  const showWidget = useCallback(() => {
    _innerShowWidget(true);
  }, []);

  return { showWidget };
};
